import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Mail, ArrowLeft, Loader2, Lock, Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface PasswordResetProps {
  onBack: () => void;
  onSuccess: () => void;
}

type Step = "email" | "otp" | "newPassword";

export const PasswordReset = ({ onBack, onSuccess }: PasswordResetProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus inputs
  useEffect(() => {
    if (step === "email") {
      emailInputRef.current?.focus();
    } else if (step === "otp") {
      otpInputRefs.current[0]?.focus();
    } else if (step === "newPassword") {
      passwordInputRef.current?.focus();
    }
  }, [step]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return t("entry.passwordMinLength");
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return t("entry.passwordRequiresLowercase");
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return t("entry.passwordRequiresUppercase");
    }
    if (!/(?=.*\d)/.test(password)) {
      return t("entry.passwordRequiresNumber");
    }
    return null;
  };

  const handleSendOTP = async (): Promise<void> => {
    if (!email.trim()) {
      setError(t("entry.pleaseEnterEmail"));
      return;
    }

    if (!validateEmail(email)) {
      setError(t("entry.pleaseEnterValidEmail"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Send OTP for password reset using signInWithOtp
      // This will send a code to the email if the user exists
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: email.toLowerCase(),
        options: {
          shouldCreateUser: false, // Don't create user if doesn't exist
        },
      });

      if (otpError) {
        if (
          otpError.message.includes("not found") ||
          otpError.message.includes("does not exist") ||
          otpError.message.includes("User not found")
        ) {
          setError(t("entry.noAccountFoundWithEmail"));
        } else {
          setError(otpError.message || t("entry.failedToSendResetCode"));
        }
        setIsLoading(false);
        return;
      }

      // Success - move to OTP step
      setStep("otp");
      setResendCooldown(60); // Set 60 second cooldown
      toast.success(t("entry.resetCodeSent"));
    } catch (err) {
      console.error("Password reset error:", err);
      setError(err instanceof Error ? err.message : t("entry.somethingWentWrong"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedOtp = value.slice(0, 6).split("").filter(char => /^\d$/.test(char));
      const newOtp = [...otp];
      pastedOtp.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      // Focus next empty input or last input
      const nextIndex = Math.min(index + pastedOtp.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
      return;
    }

    if (!/^\d$/.test(value) && value !== "") return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-advance to next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Auto-verify when all 6 digits are entered
    if (newOtp.every(digit => digit !== "") && newOtp.join("").length === 6) {
      handleOTPVerify(newOtp.join(""));
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPVerify = async (otpCode: string) => {
    if (otpCode.length !== 6) return;

    setIsVerifying(true);
    setError(null);

    try {
      // Verify OTP with email type (since we used signInWithOtp)
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: email.toLowerCase(),
        token: otpCode,
        type: "email",
      });

      if (verifyError) {
        setError(verifyError.message || t("entry.invalidVerificationCode"));
        setIsVerifying(false);
        // Clear OTP on error
        setOtp(["", "", "", "", "", ""]);
        otpInputRefs.current[0]?.focus();
        return;
      }

      // OTP verified successfully, move to new password step
      toast.success(t("entry.verificationSuccessful"));
      setStep("newPassword");
    } catch (err) {
      console.error("OTP verification error:", err);
      setError(err instanceof Error ? err.message : t("entry.verificationFailed"));
      setOtp(["", "", "", "", "", ""]);
      otpInputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async (): Promise<void> => {
    if (resendCooldown > 0) return;

    setIsResending(true);
    setError(null);
    setOtp(["", "", "", "", "", ""]);

    try {
      await handleSendOTP();
      setResendCooldown(60); // 60 second cooldown
      toast.success(t("entry.codeResent"));
    } catch (err) {
      console.error("Resend error:", err);
    } finally {
      setIsResending(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPasswordErrors({});

    // Validate passwords
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setPasswordErrors({ password: passwordError });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordErrors({ confirmPassword: t("entry.passwordsDoNotMatch") });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setError(updateError.message || t("entry.failedToResetPassword"));
        setIsLoading(false);
        return;
      }

      toast.success(t("entry.passwordResetSuccess"));
      onSuccess();
    } catch (err) {
      console.error("Password reset error:", err);
      setError(err instanceof Error ? err.message : t("entry.somethingWentWrong"));
    } finally {
      setIsLoading(false);
    }
  };

  if (step === "otp") {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="text-center">
          <h2 className="text-2xl font-bold">{t("entry.enterVerificationCode")}</h2>
          <p className="text-muted-foreground mt-2">
            {t("entry.sentCodeTo")} {email}
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex justify-center gap-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (otpInputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                disabled={isVerifying}
                className={cn(
                  "w-12 h-14 text-center text-2xl font-semibold rounded-xl bg-muted border-0 focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] outline-none transition-all",
                  error && "ring-2 ring-destructive",
                  isVerifying && "opacity-50 cursor-not-allowed"
                )}
                aria-label={`Digit ${index + 1}`}
              />
            ))}
          </div>

          {error && (
            <p className="text-sm text-destructive text-center" role="alert">
              {error}
            </p>
          )}

          {isVerifying && (
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">{t("entry.verifying")}</span>
            </div>
          )}

          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">{t("entry.didntReceiveCode")}</p>
            <button
              onClick={handleResendOTP}
              disabled={isResending || resendCooldown > 0}
              className="text-sm text-[hsl(var(--romantic-primary))] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("entry.sending")}
                </span>
              ) : resendCooldown > 0 ? (
                `${t("entry.resendIn")} ${resendCooldown}s`
              ) : (
                t("entry.resendCode")
              )}
            </button>
          </div>
        </div>

        <button
          onClick={() => setStep("email")}
          className="w-full flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("entry.back")}
        </button>
      </div>
    );
  }

  if (step === "newPassword") {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="text-center">
          <h2 className="text-2xl font-bold">{t("entry.resetPassword")}</h2>
          <p className="text-muted-foreground mt-2">{t("entry.enterNewPassword")}</p>
        </div>

        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              ref={passwordInputRef}
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPasswordErrors({ ...passwordErrors, password: undefined });
              }}
              placeholder={t("entry.newPassword")}
              disabled={isLoading}
              className={cn(
                "w-full pl-12 pr-12 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] outline-none transition-all",
                passwordErrors.password && "ring-2 ring-destructive",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
              required
              aria-label="New password"
              aria-invalid={!!passwordErrors.password}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
            {passwordErrors.password && (
              <p className="text-xs text-destructive mt-1 ml-1" role="alert">
                {passwordErrors.password}
              </p>
            )}
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setPasswordErrors({ ...passwordErrors, confirmPassword: undefined });
              }}
              placeholder={t("entry.confirmPassword")}
              disabled={isLoading}
              className={cn(
                "w-full pl-12 pr-12 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] outline-none transition-all",
                passwordErrors.confirmPassword && "ring-2 ring-destructive",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
              required
              aria-label="Confirm password"
              aria-invalid={!!passwordErrors.confirmPassword}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
            {passwordErrors.confirmPassword && (
              <p className="text-xs text-destructive mt-1 ml-1" role="alert">
                {passwordErrors.confirmPassword}
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive text-center" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              "w-full py-4 rounded-xl bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] text-white font-semibold",
              "shadow-[0_10px_40px_-10px_hsl(var(--romantic-primary)/0.25%)]",
              "hover:shadow-[0_15px_50px_-10px_hsl(var(--romantic-primary)/0.35%)]",
              "hover:scale-[1.02] active:scale-[0.98] transition-all",
              "disabled:opacity-50 flex items-center justify-center gap-2"
            )}
          >
            {isLoading && <Loader2 className="w-5 h-5 animate-spin" />}
            {isLoading ? t("entry.resetting") : t("entry.resetPassword")}
          </button>
        </form>

        <button
          onClick={() => setStep("email")}
          className="w-full flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("entry.back")}
        </button>
      </div>
    );
  }

  // Email step (default)
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center">
        <h2 className="text-2xl font-bold">{t("entry.resetPassword")}</h2>
        <p className="text-muted-foreground mt-2">{t("entry.enterEmailToReset")}</p>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            ref={emailInputRef}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            placeholder={t("entry.emailAddress")}
            disabled={isLoading}
            className={cn(
              "w-full pl-12 pr-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] outline-none transition-all",
              error && "ring-2 ring-destructive",
              isLoading && "opacity-50 cursor-not-allowed"
            )}
            required
            aria-label="Email address"
            aria-invalid={!!error}
            aria-describedby={error ? "email-reset-error" : undefined}
          />
          {error && (
            <p id="email-reset-error" className="text-xs text-destructive mt-1 ml-1" role="alert">
              {error}
            </p>
          )}
        </div>

        <button
          onClick={handleSendOTP}
          disabled={isLoading}
          className={cn(
            "w-full py-4 rounded-xl bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] text-white font-semibold",
            "shadow-[0_10px_40px_-10px_hsl(var(--romantic-primary)/0.25%)]",
            "hover:shadow-[0_15px_50px_-10px_hsl(var(--romantic-primary)/0.35%)]",
            "hover:scale-[1.02] active:scale-[0.98] transition-all",
            "disabled:opacity-50 flex items-center justify-center gap-2"
          )}
        >
          {isLoading && <Loader2 className="w-5 h-5 animate-spin" />}
          {isLoading ? t("entry.sending") : t("entry.sendResetCode")}
        </button>
      </div>

      <button
        onClick={onBack}
        className="w-full flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("entry.back")}
      </button>
    </div>
  );
};

